import { Component, type ErrorInfo, type ReactNode } from 'react'
import { AlertTriangle, Home, RefreshCw, RotateCcw } from 'lucide-react'

interface Props {
  children: ReactNode
}

interface State {
  failed: boolean
  error?: Error | null
}

export class FeatureBoundary extends Component<Props, State> {
  state: State = { failed: false, error: null }

  static getDerivedStateFromError(error: Error): State {
    return { failed: true, error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Feature render failed', error, info)
  }

  handleRetry = () => {
    this.setState({ failed: false, error: null })
  }

  handleReload = () => {
    window.location.reload()
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="flex min-h-[460px] w-full items-center justify-center p-6">
          <div className="relative w-full max-w-md overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-7 text-center shadow-lg shadow-slate-100/80">
            {/* Soft decorative glow background */}
            <div
              className="pointer-events-none absolute -top-12 left-1/2 h-32 w-32 -translate-x-1/2 rounded-full bg-rose-100/70 blur-2xl"
              aria-hidden="true"
            />

            {/* Warning / Error Icon Badge */}
            <div className="relative mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-rose-50 text-rose-500 ring-8 ring-rose-50/50">
              <AlertTriangle size={26} strokeWidth={2.2} />
            </div>

            {/* Error Message */}
            <h2 className="text-lg font-bold tracking-tight text-slate-900">
              Trang này gặp lỗi
            </h2>
            <p className="mt-2 text-xs leading-relaxed text-slate-500">
              Hãy thử tải lại trang. Các feature khác vẫn có thể dùng bình thường.
            </p>

            {/* Action buttons */}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2.5">
              <button
                type="button"
                onClick={this.handleReload}
                className="inline-flex items-center gap-1.5 rounded-xl bg-orange-600 px-4 py-2 text-xs font-semibold text-white shadow-xs transition hover:bg-orange-700 active:scale-[0.98]"
              >
                <RefreshCw size={14} />
                <span>Tải lại trang</span>
              </button>
              <button
                type="button"
                onClick={this.handleRetry}
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-700 shadow-2xs transition hover:bg-slate-50 active:scale-[0.98]"
              >
                <RotateCcw size={14} />
                <span>Thử lại</span>
              </button>
              <a
                href="/"
                className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-xs font-semibold text-slate-600 shadow-2xs transition hover:bg-slate-50 active:scale-[0.98]"
              >
                <Home size={14} />
                <span>Về trang chủ</span>
              </a>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
